import { Producto } from '../types';
import { inventarioInicial } from '../data/inventarioInicial';

class LocalDatabaseService {
  private readonly DB_KEY = 'alere_inventory_db';
  private readonly DB_VERSION = '1.0';

  constructor() {
    this.initializeDatabase();
  }

  private initializeDatabase(): void {
    const existingData = localStorage.getItem(this.DB_KEY);
    if (!existingData) {
      this.saveToLocalStorage([]);
    }
  }

  private saveToLocalStorage(productos: Producto[]): void {
    const dbData = {
      version: this.DB_VERSION,
      lastUpdated: new Date().toISOString(),
      data: productos
    };
    localStorage.setItem(this.DB_KEY, JSON.stringify(dbData));
  }

  private getFromLocalStorage(): Producto[] {
    try {
      const data = localStorage.getItem(this.DB_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        return parsed.data || [];
      }
    } catch (error) {
      console.error('Error leyendo localStorage:', error);
    }
    return [];
  }

  // Obtener todos los productos
  async getAllInventory(): Promise<Producto[]> {
    return this.getFromLocalStorage();
  }

  // Obtener un producto por ID
  async getProductById(productId: string): Promise<Producto | null> {
    const productos = this.getFromLocalStorage();
    return productos.find(p => p.id === productId) || null;
  }

  // Crear un nuevo producto
  async createProduct(producto: Producto): Promise<Producto> {
    const productos = this.getFromLocalStorage();
    productos.push(producto);
    this.saveToLocalStorage(productos);
    return producto;
  }

  // Actualizar un producto
  async updateProduct(productId: string, updates: Partial<Producto>): Promise<boolean> {
    const productos = this.getFromLocalStorage();
    const index = productos.findIndex(p => p.id === productId);
    
    if (index === -1) return false;
    
    productos[index] = { ...productos[index], ...updates };
    this.saveToLocalStorage(productos);
    return true;
  }

  // Eliminar un producto
  async deleteProduct(productId: string): Promise<boolean> {
    const productos = this.getFromLocalStorage();
    const filtered = productos.filter(p => p.id !== productId);
    
    if (filtered.length === productos.length) return false;
    
    this.saveToLocalStorage(filtered);
    return true;
  }

  // Insertar múltiples productos
  async bulkInsertInventory(productos: Producto[]): Promise<void> {
    this.saveToLocalStorage(productos);
  }

  // Buscar productos por categoría
  async getProductsByCategory(categoria: string): Promise<Producto[]> {
    const productos = this.getFromLocalStorage();
    return productos.filter(p => p.categoria === categoria);
  }

  // Buscar productos con stock bajo
  async getLowStockProducts(): Promise<Producto[]> {
    const productos = this.getFromLocalStorage();
    return productos.filter(p => p.stockFinal < p.stockMinimo);
  }

  // Limpiar toda la base de datos
  async clearDatabase(): Promise<void> {
    this.saveToLocalStorage([]);
  }

  // Verificar si hay datos
  async hasData(): Promise<boolean> {
    const productos = this.getFromLocalStorage();
    return productos.length > 0;
  }

  // Migrar datos iniciales
  async migrateInitialData(): Promise<void> {
    const hasData = await this.hasData();
    
    if (!hasData) {
      await this.bulkInsertInventory(inventarioInicial);
      console.log('Datos iniciales cargados en localStorage');
    }
  }

  // Exportar datos (para backup)
  exportData(): string {
    const data = localStorage.getItem(this.DB_KEY);
    return data || '{}';
  }

  // Importar datos (desde backup)
  importData(jsonData: string): boolean {
    try {
      const parsed = JSON.parse(jsonData);
      if (parsed.data && Array.isArray(parsed.data)) {
        this.saveToLocalStorage(parsed.data);
        return true;
      }
    } catch (error) {
      console.error('Error importando datos:', error);
    }
    return false;
  }
}

export const localDbService = new LocalDatabaseService();